// Rollover stake decider — slider + 3 recommendation rows.
//
// Two server-side sources back this surface:
//   • useStakeRecommendation() — pre-bet `rollover_full_recommendation`
//     RPC. Provides the authoritative Recommended stake (Phase Withdrawal
//     v2.2 + Kelly floor + confidence multiplier) and the Floor (20% of
//     projected_win, the RPC's hard min). This replaces the ad-hoc
//     `Math.max(opening, fullCompound * 0.3)` floor that used to live
//     here.
//   • useCompoundingDecision() — post-win confirmation RPC. Used only
//     for the "Lock" action and to detect a locked state. Once a
//     pre-bet lock RPC exists this binding can move there.
//
// Slider state is local; clicking "Lock" still goes through
// confirmDecision(custom) since that's the only write path today.
//
// Visual language: Football Products (FpKit) Kalshi-clean — compact,
// data-forward, mono uppercase labels, Bebas numerals, hairline borders,
// no gradients. Styling is inline against FpKit tokens (var(--fp-*),
// var(--text-*), var(--bg-*), var(--border*)). Data logic is unchanged.

import { useEffect, useState } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { getPhaseExplanation } from "../../rollover/phaseNarrative.ts";
import { resolveScreenState } from "../../rollover/screenState.ts";
import type {
  CompoundingDecisionState,
  CompoundingOption,
} from "../../vault/useCompoundingDecision.ts";
import type { PhantomCycleRow } from "../../phantom/usePhantomCycle.ts";
import type { RolloverChainRow } from "../../vault/useRolloverChain.ts";
import type { StakeRecommendationState } from "../../rollover/useStakeRecommendation.ts";

export interface RolloverStakeDeciderProps {
  readonly chain: RolloverChainRow | null;
  readonly cycle: PhantomCycleRow | null;
  readonly decision: CompoundingDecisionState;
  readonly recommendation: StakeRecommendationState;
}

const RATIONALE_COPY: Record<string, string> = {
  phase_withdrawal_default: "Phase Withdrawal v2.2 — week-aware stake percentage of projected win.",
  kelly_floor_active: "Kelly floor active — model probability raised the recommended stake.",
  confidence_amplified: "Signal confidence ≥ 80% — recommendation amplified ×1.10.",
  confidence_pulled_back: "Signal confidence < 65% — recommendation pulled back ×0.85.",
};

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Math.round(n).toLocaleString();
}

// ── FpKit-clean style primitives (inline) ───────────────────────
const DISPLAY = "'Bebas Neue', sans-serif";

const sx = {
  card: {
    background: "var(--bg-elev)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    overflow: "hidden" as const,
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    borderBottom: "1px solid var(--border-faint)",
  },
  headTitle: {
    fontFamily: DISPLAY,
    fontSize: 22,
    letterSpacing: "0.02em",
    color: "var(--text)",
    lineHeight: 1,
  },
  status: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
    color: "var(--text-tertiary)",
  },
  pip: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "var(--fp-accent)",
    display: "inline-block",
  },
  body: { padding: 16 },
  monoLabel: {
    fontFamily: "var(--font-mono)",
    fontSize: 9.5,
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
    color: "var(--text-tertiary)",
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 0",
    borderBottom: "1px solid var(--border-faint)",
  },
  rowWho: { minWidth: 0 },
  rowLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text)",
    letterSpacing: "0.01em",
  },
  rowSmall: {
    fontSize: 11.5,
    lineHeight: 1.45,
    color: "var(--text-tertiary)",
    marginTop: 3,
  },
  amount: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 3,
    fontFamily: DISPLAY,
    fontSize: 24,
    letterSpacing: "0.01em",
    color: "var(--text)",
    whiteSpace: "nowrap" as const,
  },
  ccy: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-tertiary)",
    letterSpacing: "0",
  },
} as const;

export function RolloverStakeDecider({
  chain,
  cycle,
  decision,
  recommendation,
}: RolloverStakeDeciderProps): JSX.Element {
  const opening = chain?.opening_stake ?? 0;
  const fullCompound = chain?.current_value ?? cycle?.stake_amount ?? 0;
  const rec = recommendation.recommendation;

  // Authoritative values from rollover_full_recommendation RPC.
  // Floor = 20% of projected_win (the RPC's hard min cap).
  // Recommended = the RPC's recommended_stake.
  // Full compound = chain's current_value (the maximum legal stake = projected_win bankroll).
  const projectedWin = rec?.projected_win ?? null;
  const recommended = rec?.recommended_stake ?? null;
  const floor = projectedWin !== null
    ? Math.max(50, Math.round(projectedWin * 0.20))
    : Math.max(opening, Math.round(fullCompound * 0.20));
  // Phase RV-UI / GAP 3 — Slider minimum MUST be the floor amount.
  // Previously this computed `min = Math.min(floor, Math.max(1, opening))`
  // which could yield GH₵1 when opening was missing, producing the
  // "Lock GH₵1" bug. The floor is the protected minimum (PRD v2.2 §6.2).
  const max = Math.max(fullCompound, floor + 1);
  const min = Math.max(1, floor);
  const odds = rec?.odds ?? chain?.odds_target ?? 1.5;

  const initial = recommended ?? Math.round((min + max) / 2);
  const [stake, setStake] = useState<number>(initial);

  useEffect(() => {
    if (recommended !== null && recommended !== undefined) {
      setStake(Math.min(max, Math.max(min, Math.round(recommended))));
    }
  }, [recommended, min, max]);

  const denom = Math.max(1, max - min);
  const pct = ((stake - min) / denom) * 100;
  const recPct =
    recommended !== null && recommended !== undefined
      ? ((Math.min(max, Math.max(min, recommended)) - min) / denom) * 100
      : 50;

  const projectedReturn = Math.round(stake * odds);
  const projectedProtect = Math.max(0, Math.round(fullCompound - stake));
  const riskBucket =
    stake < min + denom * 0.33
      ? "Conservative"
      : stake < min + denom * 0.66
        ? "Balanced"
        : "Aggressive";

  const canLock = cycle !== null && !decision.busy && decision.existingDecision === null;
  const lockOption: CompoundingOption =
    recommended !== null && stake === Math.round(recommended)
      ? "suggested"
      : stake === Math.round(fullCompound)
        ? "full"
        : stake === Math.round(opening)
          ? "original"
          : "custom";

  function handleLock(): void {
    if (!cycle || !canLock) return;
    void decision.confirmDecision(
      cycle.id,
      lockOption,
      lockOption === "custom" ? stake : undefined,
    );
  }

  const weekNum = cycle?.week_number ?? chain?.current_week ?? 1;
  const locked = decision.existingDecision !== null;

  // Phase RV-UI / GAP 2 — Screen-state gate.
  // The stake decision body only renders when the current week has
  // resolved as a win. For 'in_progress' (open / recommended / confirmed)
  // we render a polite "cycle in progress" panel. For 'bust_recovery'
  // (status = 'phantom_busted') we surface the bust recovery affordance
  // (Section 7) instead of the decision rows.
  const screenState = resolveScreenState({
    hasChain: chain !== null,
    cycleStatus: cycle?.status ?? null,
  });

  if (screenState === "in_progress") {
    return (
      <div style={sx.card}>
        <div style={sx.head}>
          <h2 style={sx.headTitle}>Week {weekNum} cycle in progress</h2>
          {cycle ? (
            <span style={sx.status}>
              <span style={sx.pip} />
              Awaiting result
            </span>
          ) : null}
        </div>
        <div style={{ padding: 16, fontSize: 13, lineHeight: 1.55, color: "var(--text-secondary)" }}>
          Cycle in progress — the stake decision opens after this week's
          slip settles. We never surface compound / floor / agent options
          before the result is in.
        </div>
      </div>
    );
  }

  if (screenState === "bust_recovery") {
    return (
      <div style={sx.card}>
        <div style={sx.head}>
          <h2 style={sx.headTitle}>Week {weekNum} bust recovery</h2>
          <span style={sx.status}>
            <span style={{ ...sx.pip, background: "var(--fp-lost)" }} />
            Chain paused
          </span>
        </div>
        <div style={{ padding: 16, fontSize: 13, lineHeight: 1.55, color: "var(--text-secondary)" }}>
          This week resolved as a loss. The chain is paused under Safe Mode
          and Section 7 bust-recovery applies — there is no stake decision
          to make until the next chain seed.
        </div>
      </div>
    );
  }

  // screenState === 'stake_decision' — render the full decision surface.
  const phaseNarrative = getPhaseExplanation({
    chainWeek: weekNum,
    suggestedStake: recommended ?? 0,
    winAmount: projectedWin ?? fullCompound,
    bankrollTotal: fullCompound,
  });

  return (
    <div style={sx.card}>
      <div style={sx.head}>
        <h2 style={sx.headTitle}>Week {weekNum} stake decision</h2>
        {cycle ? (
          <span style={sx.status}>
            <span style={{ ...sx.pip, background: locked ? "var(--fp-won)" : "var(--fp-accent)" }} />
            {locked ? "Locked" : "Awaiting lock"}
          </span>
        ) : null}
      </div>

      <div style={sx.body}>
        <div style={sx.row}>
          <div style={sx.rowWho}>
            <div style={sx.rowLabel}>Agent recommendation</div>
            <div style={sx.rowSmall}>
              {rec
                ? RATIONALE_COPY[rec.rationale] ?? RATIONALE_COPY.phase_withdrawal_default
                : recommendation.loading
                  ? "Computing recommendation…"
                  : recommendation.error
                    ? `Recommendation unavailable: ${recommendation.error}`
                    : "Awaiting upcoming odds."}
            </div>
          </div>
          <div style={{ ...sx.amount, fontSize: 28, color: "var(--fp-accent-text)" }}>
            <span style={sx.ccy}>GH₵</span>
            {fmt(recommended)}
          </div>
        </div>

        {/* Phase RV-UI / GAP 5 — phase explanation narrative (PRD v2.2 §6.1) */}
        <div
          data-testid="rollover-phase-explanation"
          style={{
            padding: "10px 12px",
            margin: "10px 0",
            fontSize: 12,
            lineHeight: 1.55,
            color: "var(--text-secondary)",
            background: "var(--bg-inset)",
            border: "1px solid var(--border-faint)",
            borderRadius: 8,
          }}
        >
          {phaseNarrative}
        </div>

        <div style={sx.row}>
          <div style={sx.rowWho}>
            <div style={sx.rowLabel}>Full compound</div>
            <div style={sx.rowSmall}>
              All prior winnings rolled forward — maximum upside, maximum downside.
            </div>
          </div>
          <div style={sx.amount}>
            <span style={sx.ccy}>GH₵</span>
            {fmt(fullCompound)}
          </div>
        </div>

        <div style={{ ...sx.row, borderBottom: "none" }}>
          <div style={sx.rowWho}>
            <div style={sx.rowLabel}>Floor (Safe Mode)</div>
            <div style={sx.rowSmall}>
              {projectedWin !== null
                ? "20% of projected win — the lowest stake the agent will recommend at any phase."
                : "Honors your loss limit and protects a share of the prior cycle's win."}
            </div>
          </div>
          <div style={sx.amount}>
            <span style={sx.ccy}>GH₵</span>
            {fmt(floor)}
          </div>
        </div>

        {/* ── Stake slider ─────────────────────────────────────── */}
        <div
          style={{
            marginTop: 16,
            padding: 14,
            background: "var(--bg-inset)",
            border: "1px solid var(--border)",
            borderRadius: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <span style={sx.monoLabel}>Your stake for Week {weekNum}</span>
            <div style={{ ...sx.amount, fontSize: 28 }}>
              <span style={sx.ccy}>GH₵</span>
              {fmt(stake)}
            </div>
          </div>

          <input
            type="range"
            min={min}
            max={max}
            step={1}
            value={stake}
            onChange={(e) => setStake(parseInt(e.target.value, 10))}
            disabled={locked || cycle === null}
            style={{
              width: "100%",
              accentColor: "var(--fp-accent)",
              cursor: locked || cycle === null ? "not-allowed" : "pointer",
            }}
          />

          <div style={{ position: "relative", height: 16, marginTop: 6 }}>
            <span
              style={{
                position: "absolute",
                left: "0%",
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                letterSpacing: "0.06em",
                color: "var(--text-quaternary)",
                whiteSpace: "nowrap",
              }}
            >
              GH₵{fmt(min)} · floor
            </span>
            {recommended !== null ? (
              <span
                style={{
                  position: "absolute",
                  left: `${recPct}%`,
                  transform: "translateX(-50%)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 9.5,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  color: "var(--fp-accent-text)",
                  whiteSpace: "nowrap",
                }}
              >
                GH₵{fmt(recommended)} · recommended
              </span>
            ) : null}
            <span
              style={{
                position: "absolute",
                left: "100%",
                transform: "translateX(-100%)",
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                letterSpacing: "0.06em",
                color: "var(--text-quaternary)",
                whiteSpace: "nowrap",
              }}
            >
              GH₵{fmt(max)} · full
            </span>
          </div>

          {/* Impact tiles */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 8,
              marginTop: 18,
            }}
          >
            <div
              style={{
                padding: "10px 12px",
                background: "var(--bg-elev)",
                border: "1px solid var(--border-faint)",
                borderRadius: 8,
              }}
            >
              <div style={sx.monoLabel}>If signal wins</div>
              <div
                style={{
                  fontFamily: DISPLAY,
                  fontSize: 20,
                  letterSpacing: "0.01em",
                  color: "var(--fp-won)",
                  marginTop: 4,
                }}
              >
                +GH₵{fmt(projectedReturn - stake)}
              </div>
            </div>
            <div
              style={{
                padding: "10px 12px",
                background: "var(--bg-elev)",
                border: "1px solid var(--border-faint)",
                borderRadius: 8,
              }}
            >
              <div style={sx.monoLabel}>If signal loses</div>
              <div
                style={{
                  fontFamily: DISPLAY,
                  fontSize: 20,
                  letterSpacing: "0.01em",
                  color: "var(--fp-lost)",
                  marginTop: 4,
                }}
              >
                −GH₵{fmt(stake)}
              </div>
            </div>
            <div
              style={{
                padding: "10px 12px",
                background: "var(--bg-elev)",
                border: "1px solid var(--border-faint)",
                borderRadius: 8,
              }}
            >
              <div style={sx.monoLabel}>Protected</div>
              <div
                style={{
                  fontFamily: DISPLAY,
                  fontSize: 20,
                  letterSpacing: "0.01em",
                  color: "var(--text)",
                  marginTop: 4,
                }}
              >
                GH₵{fmt(projectedProtect)}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              letterSpacing: "0.04em",
              color: "var(--text-tertiary)",
            }}
          >
            RISK PROFILE ·{" "}
            <strong style={{ color: "var(--text)", fontWeight: 700 }}>{riskBucket.toUpperCase()}</strong> ·
            {" "}{Math.round(pct)}% between floor and full compound
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 16px",
          borderTop: "1px solid var(--border-faint)",
        }}
      >
        <div style={{ fontSize: 11.5, lineHeight: 1.45, color: "var(--text-tertiary)", flex: 1 }}>
          Strikza never places bets. You'll copy the slip to your bookmaker manually after
          locking the stake.
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            disabled
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-quaternary)",
              cursor: "not-allowed",
              opacity: 0.6,
            }}
          >
            Pause cycle
          </button>
          <button
            type="button"
            onClick={handleLock}
            disabled={!canLock}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid var(--fp-accent)",
              background: canLock ? "var(--fp-accent)" : "var(--fp-accent-soft)",
              color: canLock ? "#fff" : "var(--fp-accent-text)",
              cursor: canLock ? "pointer" : "not-allowed",
              opacity: canLock ? 1 : 0.7,
            }}
          >
            {locked ? "Locked" : `Lock GH₵${fmt(stake)}`}
            <Icon name="arrow" size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
